import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { interviewWordingApi } from "@/api/interviewWording";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { interviewWordingKeys } from "./interviewWordingKeys";

/** The whole editor: every section, question and item, plus the version list. */
export function useInterviewWordingEditor() {
  return useQuery({
    queryKey: interviewWordingKeys.editor(),
    queryFn: () => interviewWordingApi.get(),
  });
}

/** One item's change log. Fetched only while the disclosure is open. */
export function useInterviewWordingHistory(blockKey: string, enabled: boolean) {
  return useQuery({
    queryKey: interviewWordingKeys.history(blockKey),
    queryFn: () => interviewWordingApi.history(blockKey),
    enabled,
  });
}

/**
 * Save ONE item's draft. Nothing goes live: the interview keeps reading the
 * published wording until Publish, so the served registry is untouched.
 */
export function useSaveInterviewWordingDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { blockKey: string; text: string }) =>
      interviewWordingApi.saveDraft(vars.blockKey, vars.text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: interviewWordingKeys.all });
    },
  });
}

/** Drop ONE item's unpublished draft. Nothing live changes. */
export function useDiscardInterviewWordingDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockKey: string) => interviewWordingApi.discardDraft(blockKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: interviewWordingKeys.all });
    },
  });
}

/**
 * Publish every pending draft as one new version. Changes what the interview
 * reads, so the served interview registry is stale as well as this editor.
 */
export function usePublishInterviewWording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { note: string | null }) => interviewWordingApi.publish(vars.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: interviewWordingKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.interviewRegistryAll() });
    },
  });
}

/**
 * Publish ONE item's coded default at once, as a new version, discarding its
 * draft. Changes what the interview reads, like a publish.
 */
export function useRevertInterviewWording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockKey: string) => interviewWordingApi.revert(blockKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: interviewWordingKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.interviewRegistryAll() });
    },
  });
}

/**
 * Check one item's text the way Publish would, and show the sentences it is
 * lower-cased into. A POST because the unsaved text rides the body, but it is
 * a read.
 */
export function usePreviewInterviewWording() {
  return useMutation({
    // no-invalidation: the preview writes nothing; it validates the supplied
    // text and returns the sentences it would render. Nothing cached changes.
    mutationFn: (vars: { blockKey: string; text: string }) =>
      interviewWordingApi.preview(vars.blockKey, vars.text),
  });
}
